import { ListItem, Paper, SvgIcon, Typography, Box } from '@mui/material';
import { EmployeeExtended } from '@pxtra-client-lib-legacy/gen';
import { FC, useState } from 'react';
import Icon from '../../../../../icons/svg/info.svg?react';
import { useTranslation } from 'react-i18next';
import { LandMarkIcon, MailIcon } from '../../../../../icons';
import { TooltipComponent } from '../../../../atomic/feedback';
import { Button } from '../../../../atomic/button';
import { ConfirmationDialog } from '../../../dialogs';
import {
  useDownloadEmployeeInvitationPdf,
  useSendEmployeeInvitation
} from '../../../../../../queries/employee/mutations';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { getEmployeeInvitationApiErrorMessage } from '../../../../../util/apiErrorMessage';
import { downloadBlob } from '../../../../../util';

type Props = {
  employeeId: string;
  address?: EmployeeExtended['address'];
  department?: EmployeeExtended['department'];
  user?: EmployeeExtended['user'];
  status?: EmployeeExtended['status'];
  lastName?: string | null;
  invitationDisabled?: boolean;
};

export type EmployeeProfileData = Props;

const listItemStyles = {
  padding: 0
};

const listIconStyles = {
  marginRight: '18px'
};

const lineHeight = '20px';

export const ProfileDataBox: FC<Props> = ({
  employeeId,
  address,
  department,
  user,
  status,
  lastName,
  invitationDisabled
}) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [showInvitationDialog, setShowInvitationDialog] = useState(false);
  const [showPdfInvitationDialog, setShowPdfInvitationDialog] = useState(false);
  const isResigned = status === 'RESIGNED';
  const hasWorkEmail = user?.email_origin === 'work';

  const { mutate: sendInvitation } = useSendEmployeeInvitation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee', employeeId] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      enqueueSnackbar(
        t('employee:EMPLOYEE_INVITATION_SENT', {
          count: 1
        }),
        {
          variant: 'success'
        }
      );
    },
    onError: (error: unknown) => {
      const apiMessage = getEmployeeInvitationApiErrorMessage(error, t);
      if (apiMessage) {
        enqueueSnackbar(apiMessage, { variant: 'error' });
        return;
      }
      enqueueSnackbar(
        t('employee:ERROR_SENDING_EMPLOYEE_INVITATION', {
          error: error instanceof Error ? error.message : 'Unknown error'
        }),
        {
          variant: 'error'
        }
      );
    }
  });

  const { mutate: downloadInvitationPdf } = useDownloadEmployeeInvitationPdf({
    onSuccess: (blob: Blob, variables: { id: string; filename?: string }) => {
      const safeName = `${lastName ?? 'mitarbeitende'}`
        .replaceAll(/[^A-Za-z0-9._-]+/g, '_')
        .toLowerCase();
      downloadBlob(blob, variables.filename ?? `pxtra-einladung-${safeName}.pdf`);
      queryClient.invalidateQueries({ queryKey: ['employee', employeeId] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      enqueueSnackbar(t('employee:INVITATION_PDF_GENERATED'), { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar(t('employee:INVITATION_PDF_ERROR'), { variant: 'error' });
    }
  });

  return (
    <Paper
      sx={{
        padding: '24px'
      }}
    >
      <Typography variant="h3" lineHeight="20px">
        {t('INFORMATION')}
      </Typography>
      <Box
        sx={{
          marginTop: '22px',
          '& li + *': {
            marginTop: '12px'
          }
        }}
      >
        {address && address.city && (
          <ListItem sx={listItemStyles}>
            <LandMarkIcon sx={listIconStyles} />
            <Typography
              lineHeight={lineHeight}
            >{`${address?.city}, ${address?.country}`}</Typography>
          </ListItem>
        )}
        {department && (
          <ListItem sx={listItemStyles}>
            <SvgIcon component={Icon} sx={listIconStyles} />
            <Typography lineHeight={lineHeight}>{department}</Typography>
          </ListItem>
        )}
        {user && user.email && (
          <ListItem sx={listItemStyles}>
            <MailIcon sx={listIconStyles} />
            <TooltipComponent title={user.email}>
              <Typography lineHeight={lineHeight} noWrap>
                {user.email}
              </Typography>
            </TooltipComponent>
          </ListItem>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
          <TooltipComponent
            title={invitationDisabled ? t('benefitSet:invitationPossibleIfBenefitSet') : ''}
            showOnDisabled
            arrow
          >
            <Button
              disabled={invitationDisabled}
              onClick={() =>
                hasWorkEmail ? setShowInvitationDialog(true) : setShowPdfInvitationDialog(true)
              }
            >
              {t('company:SEND_INVITATION')}
            </Button>
          </TooltipComponent>
        </Box>
      </Box>
      <ConfirmationDialog
        open={showInvitationDialog}
        onClose={() => setShowInvitationDialog(false)}
        onConfirm={() => {
          sendInvitation({ id: employeeId });
          setShowInvitationDialog(false);
        }}
        title={
          isResigned
            ? t('employee:INVITE_RESIGNED_TITLE')
            : t('company:SEND_INVITATION_FROM_PROFILE_TITLE')
        }
        contentText={isResigned ? t('employee:INVITE_RESIGNED_WARNING') : ''}
        variant={isResigned ? 'warning' : 'information'}
      />
      <ConfirmationDialog
        open={showPdfInvitationDialog}
        onClose={() => setShowPdfInvitationDialog(false)}
        onConfirm={() => {
          downloadInvitationPdf({ id: employeeId });
          setShowPdfInvitationDialog(false);
        }}
        title={t('employee:DOWNLOAD_INVITATION_PDF_TITLE')}
        contentText={
          status === 'INVITATION_PENDING_PDF'
            ? t('employee:DOWNLOAD_INVITATION_PDF_REISSUE_WARNING')
            : t('employee:INVITE_VIA_PDF_INFO')
        }
        confirmButtonText={t('employee:INVITE')}
        variant="information"
      />
    </Paper>
  );
};
